import React, { ReactElement, useMemo } from "react";

import useLocale from "hooks/common/useLocale/useLocale";
import { DocumentFile as DocumentFileType } from "interfaces/content";
import { formatDate } from "services/common/dates/dates";
import { config } from "services/common/config/config";
import FileTags from "components/common/files/FileTags/FileTags";
import FileLink from "components/common/files/FileLink/FileLink";
import { extractDocumentTags } from "components/content/Document/Document";
import documentDescriptions from "../Document/DocumentDescriptions";
import Collapsible from "components/common/ui/Collapsible/Collapsible";
import styles from "styles/styles";
import styled from "styled-components";
import useTranslate from "hooks/common/useTranslate/useTranslate";

const Excerpt = styled.div`
    min-height: 3em;
`;

const ExcerptContent = styled.div`
    font-family: "Lab Grotesque";
    font-size: 18px;
    line-height: 26px;

    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    unicode-bidi: isolate;
`;

const DocumentDate = styled.div`
    font-weight: 300;
    font-size: ${styles.fontSize.small};
    color: ${styles.colors.grey6};

    @media only screen and (${styles.breakpoints.mdGridBreakpoint}) {
        grid-column: auto;
    }
`;

const ShowMoreOrLessLink = styled.a`
    text-decoration: none;
`;

const TagsAndDate = styled.div`
    padding-top: ${styles.spacing[3]};
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const DocumentFileItem = styled.div`
    background-color: ${styles.colors.lightGrey};
    padding: ${styles.spacing[3]};
    margin-bottom: 0.5em;
`;

export interface Props {
    document: DocumentFileType;
}

function DocumentFile({ document }: Props): ReactElement {
    const locale = useLocale();
    const translate = useTranslate();
    const [collapsed, setCollapsed] = React.useState(true);

    const metadata = useMemo(
        () =>
            documentDescriptions.find((d) => d.mFileIds.includes(document.mFilesDocument.file.id)),
        [document.mFilesDocument.file.id]
    );

    const formattedDate = useMemo(
        () => formatDate(document.publishingDate, locale),
        [document.publishingDate, locale]
    );

    const tags = useMemo(() => extractDocumentTags(document, locale), [document, locale]);

    const filename = `${document.mFilesDocument.file.name}.${document.mFilesDocument.file.extension}`;
    const fileUrl = `${config.documents.endpoints.file}${document.mFilesDocument.file.id}`;

    const excerpt = (
        <Excerpt>
            <FileLink
                data-testid="document-title"
                href={fileUrl}
                download={filename}
                extension={document.mFilesDocument.file.extension}
                variant="header"
            >
                {document.title}
            </FileLink>
            {metadata?.excerpt && (
                <ExcerptContent style={!collapsed ? { display: "none" } : { display: "block" }}>
                    <div dangerouslySetInnerHTML={{ __html: metadata.excerpt }} />
                    {metadata.description && (
                        <ShowMoreOrLessLink
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setCollapsed(false);
                            }}
                        >
                            {translate("common.show.more")}
                        </ShowMoreOrLessLink>
                    )}
                </ExcerptContent>
            )}
        </Excerpt>
    );

    const description = metadata?.description ? (
        <Collapsible excerpt={excerpt} collapsed={collapsed}>
            <div dangerouslySetInnerHTML={{ __html: metadata.description }} />
            <div>
                <ShowMoreOrLessLink
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        setCollapsed(true);
                    }}
                >
                    {translate("common.show.less")}
                </ShowMoreOrLessLink>
            </div>
        </Collapsible>
    ) : (
        <Collapsible excerpt={excerpt} collapsed={collapsed}>
            <div />
        </Collapsible>
    );

    return (
        <DocumentFileItem>
            {description}
            <TagsAndDate>
                {!!tags?.length && <FileTags tags={tags} />}
                <DocumentDate data-testid="document-date">
                    {translate("common.updated")}&nbsp;{formattedDate}
                </DocumentDate>
            </TagsAndDate>
        </DocumentFileItem>
    );
}

export default DocumentFile;
